import { FormField } from '@/components';
import { Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

export interface PasswordFieldsProps {
  password: string;
  confirmPassword: string;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  confirmPasswordTouched: boolean;
  onConfirmPasswordBlur: () => void;
}

export interface PasswordValidation {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  allRequirementsMet: boolean;
  passwordsMatch: boolean;
}

export function usePasswordValidation(password: string, confirmPassword: string): PasswordValidation {
  return useMemo(() => {
    const hasMinLength = password.length >= 8;
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const allRequirementsMet = hasMinLength && hasUppercase && hasLowercase && hasNumber;
    const passwordsMatch = password === confirmPassword;

    return {
      hasMinLength,
      hasUppercase,
      hasLowercase,
      hasNumber,
      allRequirementsMet,
      passwordsMatch,
    };
  }, [password, confirmPassword]);
}

export function PasswordFields({
  password,
  confirmPassword,
  onPasswordChange,
  onConfirmPasswordChange,
  confirmPasswordTouched,
  onConfirmPasswordBlur,
}: PasswordFieldsProps) {
  const { t } = useTranslation();
  const validation = usePasswordValidation(password, confirmPassword);

  return (
    <>
      <div>
        <FormField
          label={t('auth.password', 'Password')}
          type="password"
          name="password"
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="new-password"
        />
        <div className={`space-y-1 overflow-hidden transition-all duration-300 ${validation.allRequirementsMet ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="animate-in fade-in slide-in-from-top-1 duration-300">
            <p className="text-sm text-success flex items-center gap-1">
              <Check className="size-4" />
              {t('auth.strongPassword', 'Strong password')}
            </p>
          </div>
        </div>
        <div className={`mt-2 space-y-1 overflow-hidden transition-all duration-300 ${(password.length > 0 && !validation.allRequirementsMet) ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}>
          <RequirementItem
            met={validation.hasMinLength}
            label={t('auth.passwordMinLength', 'At least 8 characters')}
          />
          <RequirementItem
            met={validation.hasUppercase}
            label={t('auth.passwordUppercase', 'One uppercase letter')}
          />
          <RequirementItem
            met={validation.hasLowercase}
            label={t('auth.passwordLowercase', 'One lowercase letter')}
          />
          <RequirementItem
            met={validation.hasNumber}
            label={t('auth.passwordNumber', 'One number')}
          />
        </div>
      </div>

      <div>
        <FormField
          label={t('auth.confirmPassword', 'Confirm Password')}
          type="password"
          name="confirmPassword"
          value={confirmPassword}
          onChange={(e) => onConfirmPasswordChange(e.target.value)}
          onBlur={onConfirmPasswordBlur}
          placeholder="••••••••"
          required
          autoComplete="new-password"
        />
        <div className={`mt-1 overflow-hidden transition-all duration-300 ${confirmPasswordTouched && confirmPassword.length > 0 ? 'max-h-10 opacity-100' : 'max-h-0 opacity-0'}`}>
          <p
            className={`text-sm flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-300 ${validation.passwordsMatch ? 'text-success' : 'text-error'}`}
          >
            {validation.passwordsMatch ? (
              <>
                <Check className="size-4" />
                {t('auth.passwordsMatch', 'Passwords match')}
              </>
            ) : (
              <>
                <X className="size-4" />
                {t('auth.passwordsDoNotMatch', 'Passwords do not match')}
              </>
            )}
          </p>
        </div>
      </div>
    </>
  );
}

function RequirementItem({ met, label }: { met: boolean; label: string }) {
  return (
    <p
      className={`text-sm flex items-center gap-1 transition-all duration-300 ${met ? 'text-success' : 'opacity-50'} animate-in fade-in slide-in-from-left-1 duration-300`}
    >
      <span className={`transition-all duration-300 ${met ? 'scale-100 opacity-100' : 'scale-90 opacity-70'}`}>
        {met ? <Check className="size-4" /> : <X className="size-4" />}
      </span>
      {label}
    </p>
  );
}
